import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import RefreshIcon from "@mui/icons-material/Refresh";
import moment from "moment";
import { Link, useParams, useSearchParams } from "react-router-dom";
import {
  TicketDetail as Ticket,
  addTicketComment,
  getTicket,
  ticketErrorMessage,
  ticketErrorStatus,
} from "../../../utils/icTicket";
import { CARD_BORDER, PRIMARY, PriorityChip, StatusChip } from "./ticketStatus";
import { SUPPORT_BASE, TICKET_VIEW_URL, isTicketView } from "./supportView";

const fullDate = (iso: string) => (iso ? moment(iso).format("MMM D, YYYY h:mm A") : "—");

// Back to the list this ticket was opened from (`?view=tickets` kept).
const BackLink = () => {
  const [searchParams] = useSearchParams();
  return (
    <Button
      component={Link}
      to={isTicketView(searchParams) ? TICKET_VIEW_URL : SUPPORT_BASE}
      startIcon={<ArrowBackIcon />}
      sx={{ textTransform: "none", color: "#5a5a66", mb: 1, ml: -1 }}
    >
      All tickets
    </Button>
  );
};

// /dashboard/support/:id — one ticket, its public comment thread and a reply
// box. Thrive users can only read and comment: status changes are the team's.
const TicketDetail = () => {
  const { id = "" } = useParams();
  const [ticket, setTicket] = useState<Ticket | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const [replyError, setReplyError] = useState<string | null>(null);
  const requestIdRef = useRef(0);

  const load = useCallback(
    (opts: { quiet?: boolean } = {}) => {
      const requestId = ++requestIdRef.current;
      if (!opts.quiet) setLoading(true);
      return getTicket(id)
        .then((t) => {
          if (requestId !== requestIdRef.current) return;
          setTicket(t);
          setError(null);
          setNotFound(false);
        })
        .catch((err) => {
          if (requestId !== requestIdRef.current) return;
          console.error("Failed to load ticket:", err);
          if (ticketErrorStatus(err) === 404) {
            setNotFound(true);
            setTicket(null);
          } else {
            setError(ticketErrorMessage(err));
          }
        })
        .finally(() => {
          if (requestId === requestIdRef.current) setLoading(false);
        });
    },
    [id],
  );

  useEffect(() => {
    load();
  }, [load]);

  const comments = useMemo(
    () =>
      [...(ticket?.comments ?? [])].sort(
        (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
      ),
    [ticket],
  );

  const sendReply = async () => {
    const content = reply.trim();
    if (!content || !ticket || sending) return;
    setSending(true);
    setReplyError(null);
    try {
      await addTicketComment(ticket.id, content);
      setReply("");
      // Refetch rather than editing state locally: the status is the team's
      // to change (a reply to a WAITING ticket leaves it WAITING).
      await load({ quiet: true });
    } catch (err) {
      setReplyError(ticketErrorMessage(err));
    } finally {
      setSending(false);
    }
  };

  if (loading && !ticket) {
    return (
      <Box sx={{ padding: "20px", textAlign: "center" }}>
        <CircularProgress sx={{ color: PRIMARY, mt: 6 }} />
      </Box>
    );
  }

  if (notFound) {
    return (
      <Box sx={{ padding: "20px", textAlign: "left" }}>
        <BackLink />
        <Paper elevation={0} sx={{ border: CARD_BORDER, borderRadius: "12px", p: 4, textAlign: "center" }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: "#2c2c34" }}>
            Ticket not found
          </Typography>
          <Typography variant="body2" sx={{ color: "#8a8a93", mt: 1 }}>
            {id} does not exist, or it was filed by someone else.
          </Typography>
        </Paper>
      </Box>
    );
  }

  if (!ticket) {
    return (
      <Box sx={{ padding: "20px", textAlign: "left" }}>
        <BackLink />
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => load()}>
              Retry
            </Button>
          }
        >
          {error || "Could not load this ticket."}
        </Alert>
      </Box>
    );
  }

  const resolved = ticket.status === "FIXED" || ticket.status === "RETEST";
  const closed = ticket.status === "CLOSED" || ticket.status === "REJECTED";

  return (
    <Box sx={{ padding: "20px", textAlign: "left", maxWidth: 980 }}>
      <BackLink />

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={{ color: "#8a8a93", fontWeight: 600 }}>
            {ticket.displayId}
          </Typography>
          <Typography
            variant="h5"
            sx={{ fontWeight: 700, color: "#2c2c34", overflowWrap: "anywhere" }}
          >
            {ticket.title}
          </Typography>
        </Box>
        <Tooltip title="Refresh">
          <span>
            <IconButton aria-label="Refresh ticket" onClick={() => load({ quiet: true })} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>

      <Stack direction="row" spacing={1} alignItems="center" useFlexGap sx={{ flexWrap: "wrap", mt: 1 }}>
        <StatusChip ticket={ticket} />
        <PriorityChip priority={ticket.priority} />
        <Typography variant="body2" sx={{ color: "#5a5a66" }}>
          Opened by <b>{ticket.createdBy || "—"}</b> on {fullDate(ticket.createdAt)}
        </Typography>
      </Stack>
      <Typography variant="body2" sx={{ color: "#8a8a93", mt: 0.5 }}>
        Last updated {fullDate(ticket.updatedAt)}
        {ticket.assignedTo ? ` · Assigned to ${ticket.assignedTo}` : ""}
      </Typography>

      <Paper elevation={0} sx={{ border: CARD_BORDER, borderRadius: "12px", p: 2, mt: 2 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#2c2c34", mb: 1 }}>
          Description
        </Typography>
        <Typography
          variant="body2"
          sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere", color: "#2c2c34" }}
        >
          {ticket.description || "No description."}
        </Typography>
      </Paper>

      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#2c2c34", mt: 3, mb: 1 }}>
        Conversation ({comments.length})
      </Typography>
      {comments.length === 0 ? (
        <Typography variant="body2" sx={{ color: "#8a8a93" }}>
          No replies yet. The support team will respond here.
        </Typography>
      ) : (
        <Stack spacing={1.5}>
          {comments.map((c) => (
            <Paper
              key={c.id}
              elevation={0}
              sx={{ border: CARD_BORDER, borderRadius: "12px", p: 2, backgroundColor: "#fcfcfd" }}
            >
              <Stack
                direction="row"
                spacing={1}
                alignItems="baseline"
                justifyContent="space-between"
                useFlexGap
                sx={{ flexWrap: "wrap" }}
              >
                <Typography variant="body2" sx={{ fontWeight: 700, color: "#2c2c34" }}>
                  {c.author || "Unknown"}
                </Typography>
                <Typography variant="caption" sx={{ color: "#8a8a93" }}>
                  {fullDate(c.createdAt)}
                </Typography>
              </Stack>
              <Typography
                variant="body2"
                sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere", mt: 0.5, color: "#2c2c34" }}
              >
                {c.content}
              </Typography>
            </Paper>
          ))}
        </Stack>
      )}

      <Box sx={{ mt: 3 }}>
        {ticket.needsReply ? (
          <Alert severity="warning" variant="filled" sx={{ mb: 2 }}>
            <AlertTitle sx={{ fontWeight: 700 }}>The team is waiting for your reply</AlertTitle>
            Please answer the latest question above. The ticket stays in "Needs your reply" until the
            team picks it up again.
          </Alert>
        ) : null}
        {resolved ? (
          <Alert severity="success" sx={{ mb: 2 }}>
            The team has marked this ticket resolved. You can't close or reopen tickets from Thrive — if
            the problem isn't fixed, reply below and the team will take another look.
          </Alert>
        ) : null}
        {closed ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            This ticket is closed. If you still need help, reply below or file a new ticket.
          </Alert>
        ) : null}
        {replyError ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {replyError}
          </Alert>
        ) : null}

        <TextField
          label="Your reply"
          placeholder="Write a reply to the support team"
          value={reply}
          onChange={(e) => setReply(e.target.value)}
          multiline
          minRows={3}
          fullWidth
          disabled={sending}
        />
        <Box sx={{ textAlign: "right", mt: 1 }}>
          <Button
            variant="contained"
            onClick={sendReply}
            disabled={!reply.trim() || sending}
            startIcon={sending ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{ backgroundColor: PRIMARY, "&:hover": { backgroundColor: "#7d184e" } }}
          >
            {sending ? "Sending…" : "Send reply"}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default TicketDetail;
